import { useState, useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import { ITrayPrintRequest } from "../typings/interfaces/print/ticket-print-request";
import { RootState } from "../redux/store";

const TRAY_PRINT_URL = "http://localhost:8535/api/print";

type TrayPrinterStatus = "idle" | "printing" | "error";
type LastError = { code: string; message: string } | null;

const useTrayPrinter = () => {
  const [status, setStatus] = useState<TrayPrinterStatus>("idle");
  const [lastError, setLastError] = useState<LastError>(null);
  // Every receipt template (check-in, pickup, reprint) prints through here, so
  // this is the one place that adds the Tax row. No tax is calculated yet -
  // when the merchant has tax enabled the row prints as $0.00. Read from the
  // store at print time (not useSelector) so the hook keeps working in trees
  // without a Redux Provider.
  const reduxContext = useContext(ReactReduxContext);

  const printTicket = useCallback(
    async (data: ITrayPrintRequest): Promise<boolean> => {
      setStatus("printing");
      setLastError(null);
      const taxEnabled = (reduxContext?.store.getState() as RootState | undefined)
        ?.merchant?.merchantListResponse?.body?.[0]?.taxEnabled;
      try {
        const res = await fetch(TRAY_PRINT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(taxEnabled ? { ...data, tax: data.tax ?? 0 } : data),
          signal: AbortSignal.timeout(15_000),
        });

        const json = await res.json().catch(() => ({ success: false, error: "Invalid response" }));

        if (!json.success) {
          setLastError({ code: "print-failed", message: json.error ?? "Print failed." });
          setStatus("error");
          return false;
        }

        setStatus("idle");
        return true;
      } catch (e: any) {
        setLastError({
          code: "network-error",
          message: e?.message ?? "Could not reach tray app.",
        });
        setStatus("error");
        return false;
      }
    },
    [reduxContext]
  );

  const resetError = useCallback(() => setLastError(null), []);

  return {
    printTicket,
    status,
    lastError,
    resetError,
    isPrinting: status === "printing",
  };
};

export default useTrayPrinter;
